import { useCallback, useEffect, useState, useMemo } from "react";
import { Table, Button, Card, Row, Col, Select, DatePicker } from "antd";
import { Api_Url } from "../../../config/config";
import axios from "axios";
import moment from "moment";
import { BranchOpeningModalAdd } from "./BranchOpeningModalAdd";
import { ErrorToast, SuccessToast } from "../../ToastComponent";
import TextArea from "antd/es/input/TextArea";

const { Option } = Select;
const { RangePicker } = DatePicker;

export const BranchOpening = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [searchFilters, setSearchFilters] = useState({
    branch: "",
    isOpenBy10AM: null,
    dateRange: null,
  });

  const fetchBranchOpeningData = async () => {
    try {
      setLoading(true);
      const userId = JSON.parse(sessionStorage.getItem("user"))?.id;
      // --- UPDATED: API Endpoint ---
      const response = await axios.get(
        `${Api_Url}api/v1/BranchOpening?UserId=${userId}&PageNumber=1&PageSize=100`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      setData(response?.data?.data || []);
    } catch (error) {
      ErrorToast("Failed to load data");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranchOpeningData();
  }, []);

  const parseDate = useCallback((dateString) => {
    if (!dateString) return null;
    const parsed = moment(dateString);
    return parsed.isValid() ? parsed : null;
  }, []);

  const branches = useMemo(() => {
    return [...new Set(data.map((item) => item.branchName).filter(Boolean))];
  }, [data]);

  const filteredData = useMemo(() => {
    let filtered = [...data];
    if (searchFilters.branch) {
      filtered = filtered.filter(
        (item) =>
          item.branchName?.toLowerCase() === searchFilters.branch.toLowerCase()
      );
    }
    // --- UPDATED: Filter property ---
    if (searchFilters.isOpenBy10AM !== null) {
      filtered = filtered.filter(
        (item) => item.isOpenBy10AM === searchFilters.isOpenBy10AM
      );
    }
    if (searchFilters.dateRange && searchFilters.dateRange.length === 2) {
      const [startDate, endDate] = searchFilters.dateRange;
      filtered = filtered.filter((item) => {
        const itemDate = parseDate(item.created);
        if (!itemDate) return false;
        return itemDate.isBetween(startDate, endDate, "day", "[]");
      });
    }
    return filtered;
  }, [data, searchFilters, parseDate]);

  const handleFilterChange = useCallback((key, value) => {
    setSearchFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setSearchFilters({
      branch: "",
      isOpenBy10AM: null,
      dateRange: null,
    });
  }, []);
  
  const handleFetchAllBranchOpenings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${Api_Url}api/v1/BranchOpening/superadmin/all-cases`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      setData(response?.data?.data || []);
      SuccessToast("All Branch Openings loaded successfully");
    } catch (error) {
      ErrorToast("Failed to load all Branch Openings");
      console.error("Error fetching all Branch Openings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportAllBranchOpeningsToCsv = async () => {
    try {
      const response = await axios.get(
        `${Api_Url}api/v1/BranchOpening/superadmin/export-all-cases`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      const file = new Blob([response.data], { type: "text/csv" });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute(
        "download",
        `BranchOpening_Report_${moment().format("YYYYMMDD")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(fileURL);
      document.body.removeChild(link);
      SuccessToast("Export successful!");
    } catch (error) {
      ErrorToast("Failed to export file");
      console.error("Error exporting:", error);
    }
  };

  const columns = [
    {
      title: "Shutter Image",
      dataIndex: "shutterImageUrl",
      render: (url) => (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img
            alt="Shutter"
            src={url}
            style={{ width: 50, height: 50, borderRadius: 4, objectFit: "cover" }}
          />
        </a>
      ),
    },
    {
      title: "Branch",
      dataIndex: "branchName",
      sorter: (a, b) => a.branchName.localeCompare(b.branchName),
    },
    {
      title: "Opened by 10 AM",
      dataIndex: "isOpenBy10AM",
      render: (isOpenBy10AM) => (
        <span
          className={`badge ${
            isOpenBy10AM ? "badge-success" : "badge-danger"
          }`}
          style={{ fontSize: "15px" }}
        >
          {isOpenBy10AM ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Reason for Delay",
      dataIndex: "reasonForDelay",
      render: (reasonForDelay) => (
        <TextArea
          style={{ width: "200px", color: "black" }}
          rows={3}
          disabled
          value={reasonForDelay}
        />
      ),
    },
    {
      title: "CBO Approval",
      dataIndex: "hasCBOApproval",
      render: (hasCBOApproval) =>
        hasCBOApproval === null ? "N/A" : (
          <span
            className={`badge ${
              hasCBOApproval ? "badge-success" : "badge-danger"
            }`}
            style={{ fontSize: "15px" }}
          >
            {hasCBOApproval ? "Yes" : "No"}
          </span>
        ),
    },
    {
      title: "CBO Approval Image",
      dataIndex: "cboApprovalImageUrl",
      render: (url) =>
        url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img
              alt="CBO Approval"
              src={url}
              style={{ width: 50, height: 50, borderRadius: 4, objectFit: "cover" }}
            />
          </a>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Location",
      render: (_, record) => (
        <a
          href={`https://maps.google.com/?q=${record.latitude},${record.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: "1.2rem" }}
        >
          <i className="fa-solid fa-location-dot text-primary"></i>
        </a>
      ),
    },
    {
      title: "Submitted On",
      dataIndex: "created",
      render: (date) => moment(date).format("DD-MM-YYYY hh:mm A"),
      sorter: (a, b) => moment(a.created) - moment(b.created),
    },
    {
      title: "Submitted By",
      dataIndex: "createdBy",
    },
  ];

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Branch Opening</h3>
            <div className="d-flex align-items-center gap-2">
              {sessionStorage.getItem("user") &&
                ["AdminFacilities", "SuperAdmin"].some((role) =>
                  JSON.parse(sessionStorage.getItem("user")).roles?.includes(
                    role
                  )
                ) && (
                  <>
                    <button
                      onClick={handleFetchAllBranchOpenings}
                      className="btn btn-info"
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Show All Cases"}
                    </button>
                    <button
                      onClick={handleExportAllBranchOpeningsToCsv}
                      className="btn btn-success"
                    >
                      <i className="fa-solid fa-file-excel me-2"></i>Export
                    </button>
                  </>
                )}
              <button className="btn btn-primary" onClick={handleOpenModal}>
                Add Branch Opening
              </button>
            </div>
          </div>
        </div>

        <Card className="my-3">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Branch"
                value={searchFilters.branch || undefined}
                onChange={(value) => handleFilterChange("branch", value)}
                allowClear
                style={{ width: "100%" }}
                showSearch
              >
                {branches.map((branch) => (
                  <Option key={branch} value={branch}>
                    {branch}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Status"
                value={searchFilters.isOpenBy10AM}
                onChange={(value) => handleFilterChange("isOpenBy10AM", value)}
                allowClear
                style={{ width: "100%" }}
              >
                <Option value={null}>All</Option>
                <Option value={true}>Yes (On Time)</Option>
                <Option value={false}>No (Delayed)</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                value={searchFilters.dateRange}
                onChange={(dates) => handleFilterChange("dateRange", dates)}
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button onClick={clearFilters} type="default">
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card>

        <div className="row">
          <div className="col-md-12">
            <div className="table-responsive">
              <Table
                className="table-striped"
                columns={columns}
                dataSource={filteredData}
                rowKey={(record) => record.id}
                loading={loading}
                pagination={{ showSizeChanger: true }}
              />
            </div>
          </div>
        </div>

        {isModalOpen && (
          <BranchOpeningModalAdd
            onClose={handleCloseModal}
            onSuccess={fetchBranchOpeningData}
          />
        )}
      </div>
    </div>
  );
};